import { useCallback } from 'react';
import type { ContactPlatform } from '@/components/contact/contact-drawer-url-state';
import type { ContactView } from '@/components/contact/contact-views';
import { useOpenContactDrawer } from '@/hooks/use-open-contact-drawer';

export interface OpenAffiliateSurfaceOptions {
  /** Selects the IG/TikTok profile view; omitted resolves to `'ig'` via the URL-state default. */
  platform?: ContactPlatform;
  /** Detail view to land on (e.g. `'affiliation'` for the program config intent). */
  view?: ContactView;
  /** Collaboration deep-link (a program id), forwarded as `contactCollab`. */
  collab?: string | null;
}

/**
 * Opens the unified contact drawer for a given affiliate `userId`.
 *
 * Routes to `open({ userId }, opts)`: `platform` selects the IG/TikTok profile
 * view (omitted → `'ig'` via the URL-state default), `view` lands the drawer on
 * a detail view (e.g. `'affiliation'`), and `collab` deep-links a program.
 *
 * Returns a stable callback so callers can pass it straight to `onClick`.
 */
export const useOpenAffiliateSurface = () => {
  const { open } = useOpenContactDrawer();

  return useCallback(
    (userId: string, opts?: OpenAffiliateSurfaceOptions) => {
      open({ userId }, { platform: opts?.platform, view: opts?.view, collab: opts?.collab });
    },
    [open]
  );
};
